import React, { useContext, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FaTimes, FaWallet, FaUniversity, FaPaperPlane, FaLock, FaExclamationCircle, FaRedo, FaCheckCircle, FaSearch, FaBackspace, FaCheck, FaChevronDown } from "react-icons/fa";
import { toast } from "react-toastify";
import AuthContext from "../../../context/AuthProvider";
import useApi from "../../../hooks/useApi";
import { transferSterlingFunds } from "../../../store/actions/walletActions";
import { useDispatch } from "react-redux";
import {
  GET_TRANSFER_META_URL,
  SETUP_PAYMENT_PIN_URL,
  VERIFY_WALLET_RECIPIENT_URL,
  VERIFY_BANK_ACCOUNT_URL,
  TRANSFER_PREVIEW_URL,
  DEFAULT_ERROR, GET_TRANSFER_Bank_URL,
} from "../../../api/urls";
import {
  TransferOverlay,
  TransferSheet,
  TransferHandle,
  TransferHeader,
  TransferHeaderTop,
  TransferTitle,
  TransferCloseBtn,
  TransferBalancePill,
  TransferBody,
  PaymentTypeRow,
  PaymentTypeCard,
  PaymentTypeIcon,
  PaymentTypeName,
  FieldGroup,
  FieldLabel,
  FieldInput,
  AmountInputWrapper,
  AmountPrefix,
  AmountInput,
  FieldHint,
  TransferFooter,
  SendButton,
  CancelTextBtn,
  SkeletonLine,
  SkeletonBlock,
  SkeletonTypeCard,
  SkeletonSection,
  PinSetupBody,
  PinSetupIcon,
  PinSetupTitle,
  PinSetupSubtitle,
  PinFieldLabel,
  PinBoxRow,
  PinBox,
  PinError,
  FetchErrorBody,
  FetchErrorIcon,
  FetchErrorTitle,
  FetchErrorMsg,
  RetryButton,
  RecipientRow,
  RecipientInputWrap,
  VerifyBtn,
  VerifiedNameBox,
  VerifiedCheckIcon,
  VerifiedName,
  VerifyErrorHint,
  ReviewBody,
  ReviewRecipientBox,
  ReviewRecipientAvatar,
  ReviewRecipientInfo,
  ReviewRecipientName,
  ReviewRecipientSub,
  ReviewBreakdownCard,
  ReviewRow,
  ReviewRowLabel,
  ReviewRowValue,
  ReviewTotalRow,
  ReviewTotalLabel,
  ReviewTotalValue,
  ReviewNote,
  ReviewSkeletonBody,
  PinDeleteBtn,
  SuccessBody,
  SuccessIconWrap,
  SuccessIconCircle,
  SuccessTitle,
  SuccessSubtitle,
  SuccessAmountBadge,
  SuccessDetailCard,
  SuccessDetailRow,
  SuccessDetailLabel,
  SuccessDetailValue,
  SuccessDoneBtn,
  BankSelectWrap,
  BankSelectTrigger,
  BankSelectValue,
  BankSelectChevron,
  BankDropdown,
  BankSearchRow,
  BankSearchIcon,
  BankSearchInput,
  BankSearchClearBtn,
  BankOptionList,
  BankOption,
  BankOptionName,
  BankOptionCheck,
  BankNoResult,
} from "./style";

const PAYMENT_TYPES = [
  { key: "wallet", label: "Wallet to Wallet", icon: <FaWallet /> },
  { key: "bank",   label: "Other Bank Transfer", icon: <FaUniversity /> },
];

const defaultForm = {
  paymentType: "wallet",
  recipientId: "",
  bankcode: "",
  accountNumber: "",
  accountName: "",
  amount: "",
  narration: "",
};

const PIN_LENGTH = 4;
const emptyPin = () => Array(PIN_LENGTH).fill("");

// ── Ghost loader ──────────────────────────────────────────────────────────────
const GhostLoader = () => (
  <>
    <SkeletonSection>
      <SkeletonLine $h="12px" $w="90px" $mb="4px" />
      <PaymentTypeRow>
        <SkeletonTypeCard />
        <SkeletonTypeCard />
      </PaymentTypeRow>
    </SkeletonSection>
    <SkeletonSection>
      <SkeletonLine $h="12px" $w="120px" />
      <SkeletonBlock $h="50px" />
    </SkeletonSection>
    <SkeletonSection>
      <SkeletonLine $h="12px" $w="100px" />
      <SkeletonBlock $h="50px" />
    </SkeletonSection>
    <SkeletonSection>
      <SkeletonLine $h="12px" $w="60px" />
      <SkeletonBlock $h="58px" />
      <SkeletonLine $h="11px" $w="160px" />
    </SkeletonSection>
  </>
);

// ── PIN input row with auto-advance ───────────────────────────────────────────
const PinInput = ({ label, value, onChange, inputRefs, onDelete }) => (
  <>
    <PinFieldLabel>{label}</PinFieldLabel>
    <PinBoxRow>
      {value.map((digit, i) => (
        <PinBox
          key={i}
          ref={(el) => { if (inputRefs) inputRefs.current[i] = el; }}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          $filled={!!digit}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/, "").slice(-1);
            const next = [...value];
            next[i] = val;
            onChange(next);
            if (val && i < PIN_LENGTH - 1) inputRefs?.current[i + 1]?.focus();
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !value[i] && i > 0) {
              inputRefs?.current[i - 1]?.focus();
            }
          }}
          onPaste={(e) => {
            const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, PIN_LENGTH);
            if (pasted.length) {
              onChange([...pasted.split(""), ...Array(PIN_LENGTH).fill("")].slice(0, PIN_LENGTH));
              inputRefs?.current[Math.min(pasted.length, PIN_LENGTH - 1)]?.focus();
            }
            e.preventDefault();
          }}
        />
      ))}
    </PinBoxRow>
    {onDelete && (
      <PinDeleteBtn type="button" onClick={onDelete} disabled={value.every((d) => !d)}>
        <FaBackspace size={15} /> Delete
      </PinDeleteBtn>
    )}
  </>
);

// ── Highlight matched text ────────────────────────────────────────────────────
const highlightMatch = (text, query) => {
  if (!query.trim()) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark>{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
};

// ── Searchable bank selector ──────────────────────────────────────────────────
const SearchableBankSelect = ({ banks, value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  const selected = banks.find((b) => b.code === value);
  const filtered = banks.filter((b) =>
    (b.name || "").toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 60);
    else setQuery("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <BankSelectWrap ref={wrapRef}>
      <BankSelectTrigger
        type="button"
        $open={open}
        disabled={disabled}
        onClick={() => { if (!disabled) setOpen((o) => !o); }}
      >
        <BankSelectValue $hasValue={!!selected}>
          {selected ? selected.name : "Select a bank"}
        </BankSelectValue>
        <BankSelectChevron $open={open}>
          <FaChevronDown size={11} />
        </BankSelectChevron>
      </BankSelectTrigger>

      {open && (
        <BankDropdown>
          <BankSearchRow>
            <BankSearchIcon><FaSearch size={12} /></BankSearchIcon>
            <BankSearchInput
              ref={searchRef}
              type="text"
              placeholder="Search banks…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <BankSearchClearBtn type="button" onClick={() => setQuery("")}>
                <FaTimes size={9} />
              </BankSearchClearBtn>
            )}
          </BankSearchRow>

          <BankOptionList>
            {filtered.length === 0 ? (
              <BankNoResult>No banks match "{query}"</BankNoResult>
            ) : (
              filtered.map((bank) => (
                <BankOption
                  key={bank.code}
                  type="button"
                  $selected={bank.code === value}
                  onClick={() => { onChange(bank.code); setOpen(false); }}
                >
                  <BankOptionName $selected={bank.code === value}>
                    {highlightMatch(bank.name, query)}
                  </BankOptionName>
                  {bank.code === value && (
                    <BankOptionCheck><FaCheckCircle size={13} /></BankOptionCheck>
                  )}
                </BankOption>
              ))
            )}
          </BankOptionList>
        </BankDropdown>
      )}
    </BankSelectWrap>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
export const SterlingTransferModal = ({ show, onClose, balance, onTransferComplete, bankTitle }) => {
  const dispatch = useDispatch();
  const api = useApi();
  const { user } = useContext(AuthContext);

  // screens: 'loading' | 'error' | 'pin-setup' | 'transfer' | 'review' | 'pin-confirm'
  const [screen, setScreen] = useState("loading");
  const [fetchError, setFetchError] = useState(null);
  const [banks, setBanks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState(defaultForm);

  // Recipient verification state (wallet-to-wallet)
  const [verifiedRecipient, setVerifiedRecipient] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState(null);

  // Bank transfer: on-demand bank list fetch
  const [isFetchingBanks, setIsFetchingBanks] = useState(false);
  const [bankFetchError, setBankFetchError] = useState(null);

  // Bank transfer: account number verification
  const [bankVerifiedAccount, setBankVerifiedAccount] = useState(null);
  const [isBankVerifying, setIsBankVerifying] = useState(false);
  const [bankVerifyError, setBankVerifyError] = useState(null);

  // Transfer preview / review state
  const [preview, setPreview] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);

  // Success state
  const [successData, setSuccessData] = useState(null);

  // Payout PIN state
  const [payoutPin, setPayoutPin] = useState(emptyPin());
  const [payoutPinError, setPayoutPinError] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const payoutPinRefs = useRef([]);

  // PIN setup state
  const [pin, setPin] = useState(emptyPin());
  const [confirmPin, setConfirmPin] = useState(emptyPin());
  const [pinError, setPinError] = useState(null);
  const [isPinSubmitting, setIsPinSubmitting] = useState(false);
  const pinRefs = useRef([]);
  const confirmPinRefs = useRef([]);

  const loadMeta = () => {
    setScreen("loading");
    setFetchError(null);
    api.get(GET_TRANSFER_META_URL)
      .then((res) => {
        setScreen(res.data?.isPaymentPin === false ? "pin-setup" : "transfer");
      })
      .catch((err) => {
        const msg = err?.response?.data?.error || err?.message || DEFAULT_ERROR;
        setFetchError(msg);
        setScreen("error");
      });
  };

  const fetchBanks = () => {
    setIsFetchingBanks(true);
    setBankFetchError(null);
    setBanks([]);
    api.get(GET_TRANSFER_Bank_URL)
      .then((res) => {
        const raw = res.data?.banks || res.data;
        const normalize = (b) => ({
          code: b.code || b.bankcode || b.bankCode || "",
          name: b.name || b.bankname || b.bankName || "",
        });
        const list = Array.isArray(raw) && raw.length ? raw.map(normalize) : [];
        setBanks(list);
      })
      .catch((err) => {
        const msg = err?.response?.data?.error || err?.message || "Failed to load bank list";
        setBankFetchError(msg);
      })
      .finally(() => setIsFetchingBanks(false));
  };

  useEffect(() => {
    if (screen === "pin-confirm") {
      setTimeout(() => payoutPinRefs.current[0]?.focus(), 50);
    }
  }, [screen]);

  useEffect(() => {
    if (!show) return;
    setForm(defaultForm);
    setVerifiedRecipient(null);
    setVerifyError(null);
    setPreview(null);
    setSuccessData(null);
    setPayoutPin(emptyPin());
    setPayoutPinError(null);
    setPin(emptyPin());
    setConfirmPin(emptyPin());
    setPinError(null);
    setBanks([]);
    setIsFetchingBanks(false);
    setBankFetchError(null);
    setBankVerifiedAccount(null);
    setIsBankVerifying(false);
    setBankVerifyError(null);
    loadMeta();
  }, [show]);

  const handleClose = () => {
    if (isLoading || isPinSubmitting || isConfirming) return;
    setForm(defaultForm);
    onClose();
  };

  // ── PIN setup submit ────────────────────────────────────────────────────────
  const handlePinSetup = async () => {
    const pinStr = pin.join("");
    const confirmStr = confirmPin.join("");

    if (pinStr.length < PIN_LENGTH) {
      setPinError("Please enter a complete 4-digit PIN");
      return;
    }
    if (pinStr !== confirmStr) {
      setPinError("PINs do not match. Please try again.");
      setConfirmPin(emptyPin());
      confirmPinRefs.current[0]?.focus();
      return;
    }

    try {
      setIsPinSubmitting(true);
      setPinError(null);
      await api.post(SETUP_PAYMENT_PIN_URL, { pin: pinStr });
      toast.success("Payment PIN created successfully");
      setPin(emptyPin());
      setConfirmPin(emptyPin());
      setScreen("transfer");
    } catch (err) {
      const msg = err?.response?.data?.error || DEFAULT_ERROR;
      setPinError(msg);
    } finally {
      setIsPinSubmitting(false);
    }
  };

  // ── Recipient verification ──────────────────────────────────────────────────
  const handleVerifyRecipient = async () => {
    const id = form.recipientId.trim();
    if (!id) return;
    try {
      setIsVerifying(true);
      setVerifyError(null);
      setVerifiedRecipient(null);
      const res = await api.post(VERIFY_WALLET_RECIPIENT_URL, { recipientId: id });
      const name = res.data?.name || res.data?.fullname || res.data?.recipientName;
      const accountNumber = res.data?.accountNumber;
      if (!name) throw new Error("Recipient not found");
      setVerifiedRecipient({ name, id,accountNumber });
    } catch (err) {
      setVerifyError(err?.response?.data?.error || err?.message || "Recipient not found. Please check the ID or email.");
    } finally {
      setIsVerifying(false);
    }
  };

  // ── Bank account verification ───────────────────────────────────────────────
  const handleVerifyBankAccount = async () => {
    if (form.accountNumber.length < 10 || !form.bankcode) return;
    try {
      setIsBankVerifying(true);
      setBankVerifyError(null);
      setBankVerifiedAccount(null);
      setForm((prev) => ({ ...prev, accountName: "" }));
      const res = await api.post(VERIFY_BANK_ACCOUNT_URL, {
        accountNumber: form.accountNumber,
        bankcode: form.bankcode,
      });
      const name = res.data?.accountName;
      if (!name) throw new Error("Account not found");
      setBankVerifiedAccount({ accountName: name,
      ...res.data});
      setForm((prev) => ({ ...prev, accountName: name }));
    } catch (err) {
      setBankVerifyError(
        err?.response?.data?.error || err?.message || "Account not found. Check the number and bank."
      );
    } finally {
      setIsBankVerifying(false);
    }
  };

  // ── Transfer form change ────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "recipientId") {
      setVerifiedRecipient(null);
      setVerifyError(null);
    }
    if (name === "accountNumber") {
      setBankVerifiedAccount(null);
      setBankVerifyError(null);
      setForm((prev) => ({ ...prev, accountNumber: value, accountName: "" }));
      return;
    }
    if (name === "bankcode") {
      setBankVerifiedAccount(null);
      setBankVerifyError(null);
      setForm((prev) => ({ ...prev, bankcode: value, accountName: "" }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ── Step 1: Preview (quote) ─────────────────────────────────────────────────
  const handlePreview = async () => {
    const isWallet = form.paymentType === "wallet";
    if (isWallet && !form.recipientId.trim()) {
      toast.error("Please enter the recipient wallet Account Number or email");
      return;
    }
    if (!isWallet && !form.accountNumber) {
      toast.error("Please enter the recipient account number");
      return;
    }
    if (!form.amount || +form.amount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }


    setIsPreviewing(true);
    try {
      const bank = banks.find((b) => b.code === form.bankcode);
      const res = await api.post(TRANSFER_PREVIEW_URL, {
        paymentType: form.paymentType,
        recipientId: form.recipientId,
        bankCode: form.bankcode,
        bankName: bank?.name,
        accountNumber: form.accountNumber,
        accountName: form.accountName,
        amount: form.amount,
        narration: form.narration,
        accountNumberV: verifiedRecipient?.accountNumber,
      });
      setPreview(res.data);
      setScreen("review");
    } catch (error) {
      toast.error(error?.response?.data?.error || DEFAULT_ERROR);
    } finally {
      setIsPreviewing(false);
    }
  };

  // ── Step 2: Confirm with payout PIN ────────────────────────────────────────
  const handleConfirm = async () => {
    const pinStr = payoutPin.join("");
    if (pinStr.length < PIN_LENGTH) {
      setPayoutPinError("Please enter your 4-digit payment PIN");
      return;
    }
    setIsConfirming(true);
    setPayoutPinError(null);
    try {
      const result = await dispatch(
        transferSterlingFunds({
          user,
          paymentType: form.paymentType,
          recipientId: form.recipientId,
          bankCode: form.bankcode,
          accountNumber: form.accountNumber,
          accountNumberV: verifiedRecipient?.accountNumber,
          accountName: form.accountName,
          amount: form.amount,
          narration: form.narration,
          pin: pinStr,
          reference: preview?.reference,
          ...bankVerifiedAccount
        })
      ).unwrap();
      if (result?.completed) {
        setSuccessData(result);
        setScreen("success");
      } else {
        toast.success("Transfer initiated successfully");
        setForm(defaultForm);
        onClose();
        if (onTransferComplete) onTransferComplete();
      }
    } catch (error) {
      // .unwrap() throws the rejectWithValue payload — a plain string
      setPayoutPinError(typeof error === "string" ? error : DEFAULT_ERROR);
    } finally {
      setIsConfirming(false);
    }
  };

  if (!show) return null;

  const isWallet = form.paymentType === "wallet";
  const recipientReady = isWallet ? !!verifiedRecipient : (!!bankVerifiedAccount && banks.length > 0 && !isFetchingBanks);
  const canSubmit = screen === "transfer" && !isLoading && recipientReady && !!form.amount && +form.amount > 0;
  const pinComplete = pin.every(Boolean);
  const confirmPinComplete = confirmPin.every(Boolean);

  return ReactDOM.createPortal(
    <TransferOverlay onClick={handleClose}>
      <TransferSheet onClick={(e) => e.stopPropagation()}>
        <TransferHandle />

        <TransferHeader>
          <TransferHeaderTop>
            <TransferTitle>
              {screen === "pin-setup" ? "Secure Your Transfers"
                : screen === "review" ? "Review Transfer"
                : screen === "pin-confirm" ? "Enter Payment PIN"
                : screen === "success" ? "Payment Sent"
                : "Send Money"}
            </TransferTitle>
            <TransferCloseBtn onClick={handleClose} disabled={isLoading || isPinSubmitting || isConfirming}>
              <FaTimes size={14} />
            </TransferCloseBtn>
          </TransferHeaderTop>
          {screen !== "pin-setup" && screen !== "review" && screen !== "pin-confirm" && screen !== "success" && (
            <TransferBalancePill>
              <span>Available</span>
              <span>₦{Number(balance || 0).toLocaleString()}</span>
            </TransferBalancePill>
          )}
        </TransferHeader>

        {/* ── Loading screen ── */}
        {screen === "loading" && (
          <TransferBody><GhostLoader /></TransferBody>
        )}

        {/* ── Error screen ── */}
        {screen === "error" && (
          <FetchErrorBody>
            <FetchErrorIcon><FaExclamationCircle /></FetchErrorIcon>
            <FetchErrorTitle>Could not load transfer details</FetchErrorTitle>
            <FetchErrorMsg>{fetchError || "Something went wrong. Please check your connection and try again."}</FetchErrorMsg>
            <RetryButton onClick={loadMeta}>
              <FaRedo size={12} /> Try Again
            </RetryButton>
          </FetchErrorBody>
        )}

        {/* ── PIN setup screen ── */}
        {screen === "pin-setup" && (
          <>
            <PinSetupBody>
              <PinSetupIcon><FaLock /></PinSetupIcon>
              <PinSetupTitle>Create Payment PIN</PinSetupTitle>
              <PinSetupSubtitle>
                Set up a 4-digit PIN to authorise transfers from your {bankTitle} wallet.
              </PinSetupSubtitle>

              {pinError && <PinError>{pinError}</PinError>}

              <PinInput
                label="New PIN"
                value={pin}
                onChange={(v) => { setPin(v); setPinError(null); }}
                inputRefs={pinRefs}
              />
              <PinInput
                label="Confirm PIN"
                value={confirmPin}
                onChange={(v) => { setConfirmPin(v); setPinError(null); }}
                inputRefs={confirmPinRefs}
              />
            </PinSetupBody>

            <TransferFooter>
              <SendButton
                onClick={handlePinSetup}
                disabled={!pinComplete || !confirmPinComplete || isPinSubmitting}
              >
                {isPinSubmitting ? "Setting up…" : "Create PIN & Continue"}
              </SendButton>
              <CancelTextBtn onClick={handleClose} disabled={isPinSubmitting}>
                Cancel
              </CancelTextBtn>
            </TransferFooter>
          </>
        )}

        {/* ── Transfer screen ── */}
        {screen === "transfer" && (
          <>
            <TransferBody>
              <FieldGroup>
                <FieldLabel>Payment Type</FieldLabel>
                <PaymentTypeRow>
                  {PAYMENT_TYPES.map(({ key, label, icon }) => (
                    <PaymentTypeCard
                      key={key}
                      type="button"
                      $active={form.paymentType === key}
                      onClick={() => {
                        if (form.paymentType === key) return;
                        setVerifiedRecipient(null);
                        setVerifyError(null);
                        setBankVerifiedAccount(null);
                        setBankVerifyError(null);
                        setBankFetchError(null);
                        setForm((prev) => ({ ...prev, paymentType: key, recipientId: "", accountNumber: "", accountName: "" }));
                        if (key === "bank") fetchBanks();
                      }}
                    >
                      <PaymentTypeIcon $active={form.paymentType === key}>{icon}</PaymentTypeIcon>
                      <PaymentTypeName $active={form.paymentType === key}>{label}</PaymentTypeName>
                    </PaymentTypeCard>
                  ))}
                </PaymentTypeRow>
              </FieldGroup>

              {isWallet ? (
                <FieldGroup>
                  <FieldLabel>Recipient Wallet Account Number or Email</FieldLabel>
                  <RecipientRow>
                    <RecipientInputWrap>
                      <FieldInput
                        type="text"
                        name="recipientId"
                        value={form.recipientId}
                        onChange={handleChange}
                        placeholder="Enter wallet account number or email address"
                        autoComplete="off"
                        style={{ width: "100%" }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && form.recipientId.trim() && !isVerifying) {
                            e.preventDefault();
                            handleVerifyRecipient();
                          }
                        }}
                      />
                    </RecipientInputWrap>
                    <VerifyBtn
                      type="button"
                      onClick={handleVerifyRecipient}
                      disabled={!form.recipientId.trim() || isVerifying}
                    >
                      {isVerifying ? "…" : verifiedRecipient ? <FaCheckCircle size={14} /> : <><FaSearch size={12} /> Verify</>}
                    </VerifyBtn>
                  </RecipientRow>
                  {verifiedRecipient && (
                    <VerifiedNameBox>
                      <VerifiedCheckIcon><FaCheckCircle /></VerifiedCheckIcon>
                      <VerifiedName>{verifiedRecipient.name}</VerifiedName>
                    </VerifiedNameBox>
                  )}
                  {verifyError && <VerifyErrorHint>{verifyError}</VerifyErrorHint>}
                </FieldGroup>
              ) : (
                <>
                  {/* Loading bank list */}
                  {isFetchingBanks && (
                    <>
                      <FieldGroup>
                        <SkeletonLine $h="12px" $w="110px" $mb="6px" />
                        <SkeletonBlock $h="50px" />
                      </FieldGroup>
                      <FieldGroup>
                        <SkeletonLine $h="12px" $w="130px" $mb="6px" />
                        <SkeletonBlock $h="50px" />
                      </FieldGroup>
                    </>
                  )}

                  {/* Bank list fetch error */}
                  {!isFetchingBanks && bankFetchError && (
                    <FieldGroup>
                      <VerifyErrorHint>{bankFetchError}</VerifyErrorHint>
                      <VerifyBtn
                        type="button"
                        onClick={fetchBanks}
                        style={{ marginTop: 8, width: "100%" }}
                      >
                        <FaRedo size={11} /> Retry
                      </VerifyBtn>
                    </FieldGroup>
                  )}

                  {/* Banks loaded */}
                  {!isFetchingBanks && !bankFetchError && banks.length > 0 && (
                    <>
                      <FieldGroup>
                        <FieldLabel>Recipient Bank</FieldLabel>
                        <SearchableBankSelect
                          banks={banks}
                          value={form.bankcode}
                          onChange={(code) => {
                            setBankVerifiedAccount(null);
                            setBankVerifyError(null);
                            setForm((prev) => ({ ...prev, bankcode: code, accountName: "" }));
                          }}
                        />
                      </FieldGroup>

                      <FieldGroup>
                        <FieldLabel>Account Number</FieldLabel>
                        <RecipientRow>
                          <RecipientInputWrap>
                            <FieldInput
                              type="tel"
                              name="accountNumber"
                              maxLength={10}
                              value={form.accountNumber}
                              onChange={handleChange}
                              placeholder="10-digit account number"
                              inputMode="numeric"
                              style={{ width: "100%" }}
                            />
                          </RecipientInputWrap>
                          <VerifyBtn
                            type="button"
                            onClick={handleVerifyBankAccount}
                            disabled={form.accountNumber.length < 10 || isBankVerifying}
                          >
                            {isBankVerifying
                              ? "…"
                              : bankVerifiedAccount
                              ? <FaCheckCircle size={14} />
                              : <><FaSearch size={12} /> Verify</>}
                          </VerifyBtn>
                        </RecipientRow>
                        {bankVerifiedAccount && (
                          <VerifiedNameBox>
                            <VerifiedCheckIcon><FaCheckCircle /></VerifiedCheckIcon>
                            <VerifiedName>{bankVerifiedAccount.accountName}</VerifiedName>
                          </VerifiedNameBox>
                        )}
                        {bankVerifyError && <VerifyErrorHint>{bankVerifyError}</VerifyErrorHint>}
                      </FieldGroup>

                      <FieldGroup>
                        <FieldLabel>Account Name</FieldLabel>
                        <FieldInput
                          type="text"
                          name="accountName"
                          value={form.accountName}
                          readOnly
                          placeholder="Auto-filled after verification"
                          style={{ background: "#f8fafc", color: form.accountName ? "#0f172a" : "#94a3b8", cursor: "default" }}
                        />
                      </FieldGroup>
                    </>
                  )}
                </>
              )}

              {(() => {
                const fieldsLocked = isWallet ? !verifiedRecipient : !bankVerifiedAccount;
                return (
                  <>
                    <FieldGroup style={{ opacity: fieldsLocked ? 0.4 : 1, transition: "opacity 0.2s ease" }}>
                      <FieldLabel>Amount</FieldLabel>
                      <AmountInputWrapper>
                        <AmountPrefix>₦</AmountPrefix>
                        <AmountInput
                          type="number"
                          name="amount"
                          value={form.amount}
                          onChange={handleChange}
                          placeholder="0.00"
                          inputMode="decimal"
                          min="1"
                          disabled={fieldsLocked}
                        />
                      </AmountInputWrapper>
                      {fieldsLocked && (
                        <FieldHint>Verify the recipient first to enable this field</FieldHint>
                      )}
                    </FieldGroup>

                    <FieldGroup style={{ opacity: fieldsLocked ? 0.4 : 1, transition: "opacity 0.2s ease" }}>
                      <FieldLabel>
                        Narration{" "}
                        <span style={{ color: "#94a3b8", fontWeight: 400 }}>(optional)</span>
                      </FieldLabel>
                      <FieldInput
                        disabled={fieldsLocked}
                        type="text"
                        name="narration"
                        value={form.narration}
                        onChange={handleChange}
                        placeholder="What's this payment for?"
                      />
                    </FieldGroup>
                  </>
                );
              })()}
            </TransferBody>

            <TransferFooter>
              <SendButton onClick={handlePreview} disabled={!canSubmit || isPreviewing}>
                {isPreviewing ? "Getting quote…" : (
                  <>
                    <FaPaperPlane size={13} />
                    {form.amount ? `Send ₦${Number(form.amount).toLocaleString()}` : "Send Money"}
                  </>
                )}
              </SendButton>
              <CancelTextBtn onClick={handleClose} disabled={isPreviewing}>
                Cancel
              </CancelTextBtn>
            </TransferFooter>
          </>
        )}

        {/* ── Review screen ── */}
        {screen === "review" && (
          <>
            <ReviewBody>
              <ReviewRecipientBox>
                <ReviewRecipientAvatar>
                  {form.paymentType === "wallet" ? <FaWallet size={20} /> : <FaUniversity size={20} />}
                </ReviewRecipientAvatar>
                <ReviewRecipientInfo>
                  <ReviewRecipientName>
                    {form.paymentType === "wallet"
                      ? (verifiedRecipient?.name || form.recipientId)
                      : (form.accountName || form.accountNumber)}
                  </ReviewRecipientName>
                  <ReviewRecipientSub>
                    {form.paymentType === "wallet"
                      ? `Wallet • ${form.recipientId}`
                      : `${banks.find((b) => b.code === form.bankcode)?.name || ""} • ${form.accountNumber}`}
                  </ReviewRecipientSub>
                </ReviewRecipientInfo>
              </ReviewRecipientBox>

              <ReviewBreakdownCard>
                <ReviewRow>
                  <ReviewRowLabel>Amount</ReviewRowLabel>
                  <ReviewRowValue>₦{Number(preview?.amount ?? form.amount).toLocaleString()}</ReviewRowValue>
                </ReviewRow>
                {preview?.fee != null && (
                  <ReviewRow>
                    <ReviewRowLabel>Transfer Fee</ReviewRowLabel>
                    <ReviewRowValue>₦{Number(preview.fee).toLocaleString()}</ReviewRowValue>
                  </ReviewRow>
                )}
                {preview?.vat != null && (
                  <ReviewRow>
                    <ReviewRowLabel>VAT</ReviewRowLabel>
                    <ReviewRowValue>₦{Number(preview.vat).toLocaleString()}</ReviewRowValue>
                  </ReviewRow>
                )}
                <ReviewTotalRow>
                  <ReviewTotalLabel>Total Debit</ReviewTotalLabel>
                  <ReviewTotalValue>
                    ₦{Number(preview?.total ?? preview?.amount ?? form.amount).toLocaleString()}
                  </ReviewTotalValue>
                </ReviewTotalRow>
              </ReviewBreakdownCard>

              {form.narration ? (
                <ReviewNote>"{form.narration}"</ReviewNote>
              ) : null}
            </ReviewBody>

            <TransferFooter>
              <SendButton onClick={() => setScreen("pin-confirm")}>
                Next — Enter PIN
              </SendButton>
              <CancelTextBtn onClick={() => setScreen("transfer")}>
                Back
              </CancelTextBtn>
            </TransferFooter>
          </>
        )}

        {/* ── Payout PIN screen ── */}
        {screen === "pin-confirm" && (
          <>
            <PinSetupBody>
              <PinSetupIcon><FaLock /></PinSetupIcon>
              <PinSetupTitle>Confirm with PIN</PinSetupTitle>
              <PinSetupSubtitle>
                Enter your 4-digit payment PIN to authorise this transfer of{" "}
                ₦{Number(preview?.total ?? preview?.amount ?? form.amount).toLocaleString()}.
              </PinSetupSubtitle>

              {payoutPinError && <PinError>{payoutPinError}</PinError>}

              <PinInput
                label="Payment PIN"
                value={payoutPin}
                onChange={(v) => { setPayoutPin(v); setPayoutPinError(null); }}
                inputRefs={payoutPinRefs}
                onDelete={() => {
                  const lastIdx = [...payoutPin].map((d, i) => (d ? i : -1)).filter((i) => i !== -1).pop() ?? -1;
                  if (lastIdx === -1) return;
                  const next = [...payoutPin];
                  next[lastIdx] = "";
                  setPayoutPin(next);
                  setPayoutPinError(null);
                  payoutPinRefs.current[lastIdx]?.focus();
                }}
              />
            </PinSetupBody>

            <TransferFooter>
              <SendButton
                onClick={handleConfirm}
                disabled={!payoutPin.every(Boolean) || isConfirming}
              >
                {isConfirming ? "Processing…" : "Confirm & Send"}
              </SendButton>
              <CancelTextBtn onClick={() => setScreen("review")} disabled={isConfirming}>
                Back
              </CancelTextBtn>
            </TransferFooter>
          </>
        )}

        {/* ── Success screen ── */}
        {screen === "success" && (() => {
          const sentAmount = successData?.amount ?? preview?.total ?? preview?.amount ?? form.amount;
          const recipientName = form.paymentType === "wallet"
            ? (verifiedRecipient?.name || form.recipientId)
            : (form.accountName || form.accountNumber);
          const bankName = form.paymentType === "wallet"
            ? "Wallet Transfer"
            : banks.find((b) => b.code === form.bankcode)?.name;
          const ref = successData?.reference ?? preview?.reference;
          const now = new Date().toLocaleString("en-NG", { dateStyle: "medium", timeStyle: "short" });

          return (
            <>
              <SuccessBody>
                <SuccessIconWrap>
                  <SuccessIconCircle>
                    <FaCheck size={38} color="#fff" />
                  </SuccessIconCircle>
                </SuccessIconWrap>

                <SuccessTitle>Payment Sent!</SuccessTitle>
                <SuccessSubtitle>
                  Your transfer to <strong>{recipientName}</strong> was successful.
                </SuccessSubtitle>

                <SuccessAmountBadge>
                  <span>₦{Number(sentAmount).toLocaleString()}</span>
                </SuccessAmountBadge>

                <SuccessDetailCard>
                  <SuccessDetailRow>
                    <SuccessDetailLabel>Recipient</SuccessDetailLabel>
                    <SuccessDetailValue>{recipientName}</SuccessDetailValue>
                  </SuccessDetailRow>
                  {bankName && (
                    <SuccessDetailRow>
                      <SuccessDetailLabel>Via</SuccessDetailLabel>
                      <SuccessDetailValue>{bankName}</SuccessDetailValue>
                    </SuccessDetailRow>
                  )}
                  {form.narration ? (
                    <SuccessDetailRow>
                      <SuccessDetailLabel>Narration</SuccessDetailLabel>
                      <SuccessDetailValue>{form.narration}</SuccessDetailValue>
                    </SuccessDetailRow>
                  ) : null}
                  {ref ? (
                    <SuccessDetailRow>
                      <SuccessDetailLabel>Reference</SuccessDetailLabel>
                      <SuccessDetailValue>{ref}</SuccessDetailValue>
                    </SuccessDetailRow>
                  ) : null}
                  <SuccessDetailRow>
                    <SuccessDetailLabel>Date</SuccessDetailLabel>
                    <SuccessDetailValue>{now}</SuccessDetailValue>
                  </SuccessDetailRow>
                </SuccessDetailCard>

                <SuccessDoneBtn
                  onClick={() => {
                    onClose();
                    if (onTransferComplete) onTransferComplete();
                  }}
                >
                  Done
                </SuccessDoneBtn>
              </SuccessBody>
            </>
          );
        })()}
      </TransferSheet>
    </TransferOverlay>,
    document.body
  );
};
